import { Button } from '@crowdfpl/ui-web/button';
import { Plus } from 'lucide-react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { useAuth } from '@/hooks/auth';
import { useCreateDuel } from '@/hooks/data/duels';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import { useAuthModal } from '@/stores/ui';
import type { PlayerDto } from '@/types/players';

interface CompactDuelSuggestionProps {
  playerA: PlayerDto;
  playerB: PlayerDto;
  gameweekId?: string;
  onCreated?: () => void;
  isNextWeek?: boolean;
  className?: string;
}

export function CompactDuelSuggestion({
  playerA,
  playerB,
  gameweekId,
  onCreated,
  isNextWeek = false,
  className,
}: CompactDuelSuggestionProps) {
  const create = useCreateDuel();
  const { isAuthenticated } = useAuth();
  const { showAuthModal } = useAuthModal();
  const isMobile = useIsMobile();

  const hasTwoPlayers = playerA && playerB;
  const isSelfDuel = playerA && playerB && playerA.id === playerB.id;
  const canCreateDuel = hasTwoPlayers && !isSelfDuel && isNextWeek;

  const handleCreate = () => {
    trackUmamiEvent('duel_create_submit_click', {
      playerAId: playerA.id,
      playerBId: playerB.id,
      isAuthenticated,
    });

    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    if (!playerA.id || !playerB.id) return;
    create.mutate(
      { player_a_id: playerA.id, player_b_id: playerB.id, gameweek_id: gameweekId },
      { onSuccess: onCreated },
    );
  };

  const buttonLabel = create.isPending
    ? 'Creating...'
    : `Create ${playerA.web_name} vs ${playerB.web_name}`;

  // Same layout for mobile and desktop
  return (
    <div className={cn('flex justify-center py-8', className)}>
      <div className="flex flex-col items-center text-center max-w-[520px] w-full">
        <p className="text-base font-medium text-foreground">
          No duels found for this selection
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {isNextWeek ? 'Select different players or create a new duel' : 'Select different players'}
        </p>

        {canCreateDuel && (
          <Button
            onClick={handleCreate}
            disabled={create.isPending}
            className={cn('mt-4', isMobile ? 'w-full h-9 text-sm' : 'h-10')}
          >
            <Plus className="h-4 w-4 mr-2" />
            {buttonLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
